import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Category } from '../../types/Category';
import {
  categorySubtreeHasSelectableCompareTarget,
  countContentRowsInCategorySubtree,
  collectCompareContentTargetGroups,
  documentHasCompareTargets,
  findDefaultAddMatchingContentSourceIdAmongGroups,
  findCategoryInTree,
  findDocumentRootCategory,
  resolveCompareTargetForCategory,
  type AddMatchingContentSourceGroup,
  type CompareContentTarget,
} from '../../utils/CategoryUtils';
import {
  CompareContentMatchRule,
  CompareContentPreview,
  CompareContentScopeType,
  previewCompareContent,
} from '../../services/CategoryService';

interface CompareContentDialogProps {
  open: boolean;
  onClose: () => void;
  sourceCategory: Category | null;
  categoriesTree: Category[];
  onSelectContentIds: (contentIds: string[]) => void;
  /** Opens Split category with these rows in the first subcategory; the rest go to the second. */
  onSplitWithContentIds: (
    contentIds: string[],
    whichInFirst: 'matched' | 'unmatched',
  ) => void;
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && 'message' in data) {
      const msg = (data as { message?: unknown }).message;
      if (typeof msg === 'string' && msg.trim()) return msg;
    }
  }
  return fallback;
}

export type CategoryPickerTreeLabelProps = {
  category: Category;
  contentCount: number;
  nameColor?: string;
  nameSuffix?: string;
};

/** Shared tree row: icon, name, (count) — LTR layout like English. */
export function CategoryPickerTreeLabel({
  category,
  contentCount,
  nameColor = 'text.primary',
  nameSuffix = '',
}: CategoryPickerTreeLabelProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        py: 0.25,
        width: '100%',
        minWidth: 0,
        direction: 'ltr',
      }}
    >
      {category.contentCategory ? (
        <DescriptionOutlinedIcon sx={{ fontSize: 18, opacity: 0.85, flexShrink: 0 }} />
      ) : (
        <FolderIcon sx={{ fontSize: 18, opacity: 0.85, flexShrink: 0 }} />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Typography
          component="span"
          variant="body2"
          dir="auto"
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: nameColor,
          }}
        >
          {category.name}
          {nameSuffix}
        </Typography>
        <Typography
          component="span"
          variant="body2"
          dir="ltr"
          sx={{ flexShrink: 0, color: 'text.secondary' }}
        >
          ({contentCount})
        </Typography>
      </Box>
    </Box>
  );
}

function collectAncestorCategoryIds(categoriesTree: Category[], categoryId: string): string[] {
  const ids: string[] = [];
  let parentId = findCategoryInTree(categoriesTree, categoryId)?.parentId;
  while (parentId) {
    ids.push(parentId);
    parentId = findCategoryInTree(categoriesTree, parentId)?.parentId;
  }
  return ids;
}

const CompareContentDialog: React.FC<CompareContentDialogProps> = ({
  open,
  onClose,
  sourceCategory,
  categoriesTree,
  onSelectContentIds,
  onSplitWithContentIds,
}) => {
  const [selectedTargetCategoryId, setSelectedTargetCategoryId] = useState('');
  const [matchRule, setMatchRule] = useState<CompareContentMatchRule>('EQUIVALENT');
  const [preview, setPreview] = useState<CompareContentPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [splitIntoSubcategories, setSplitIntoSubcategories] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previewRequestGenRef = useRef(0);

  const documentRoot = useMemo(
    () =>
      sourceCategory
        ? findDocumentRootCategory(categoriesTree, sourceCategory.id)
        : null,
    [categoriesTree, sourceCategory],
  );

  const hasTargets = useMemo(
    () => (sourceCategory ? documentHasCompareTargets(categoriesTree, sourceCategory) : false),
    [categoriesTree, sourceCategory],
  );

  const addMatchingStyleGroups = useMemo((): AddMatchingContentSourceGroup[] => {
    if (!sourceCategory) return [];
    return collectCompareContentTargetGroups(categoriesTree, sourceCategory).map(g => ({
      ancestorId: g.ancestorId,
      ancestorName: g.ancestorName,
      depth: g.depth,
      sources: g.targets
        .filter((t): t is CompareContentTarget & { kind: 'contentCategory' } => t.kind === 'contentCategory')
        .map(t => t.category),
    }));
  }, [categoriesTree, sourceCategory]);

  const defaultTargetCategoryId = useMemo(() => {
    if (!sourceCategory) return '';
    return (
      findDefaultAddMatchingContentSourceIdAmongGroups(addMatchingStyleGroups, sourceCategory.id) ??
      ''
    );
  }, [sourceCategory, addMatchingStyleGroups]);

  const selectedTarget = useMemo((): CompareContentTarget | null => {
    if (!sourceCategory || !selectedTargetCategoryId) return null;
    const cat = findCategoryInTree(categoriesTree, selectedTargetCategoryId);
    if (!cat) return null;
    return resolveCompareTargetForCategory(cat, sourceCategory.id);
  }, [sourceCategory, selectedTargetCategoryId, categoriesTree]);

  const defaultExpanded = useMemo(() => {
    if (!documentRoot || !sourceCategory) return [];
    const ids = new Set<string>([documentRoot.id, sourceCategory.id]);
    collectAncestorCategoryIds(categoriesTree, sourceCategory.id).forEach(id => ids.add(id));
    if (selectedTargetCategoryId) {
      ids.add(selectedTargetCategoryId);
      collectAncestorCategoryIds(categoriesTree, selectedTargetCategoryId).forEach(id => ids.add(id));
    }
    return Array.from(ids);
  }, [documentRoot, sourceCategory, selectedTargetCategoryId, categoriesTree]);

  const refreshPreview = useCallback(async () => {
    if (!sourceCategory || !selectedTarget) {
      setPreview(null);
      setError(null);
      return;
    }
    const requestGen = ++previewRequestGenRef.current;
    setPreviewLoading(true);
    setError(null);
    try {
      const scopeType: CompareContentScopeType =
        selectedTarget.kind === 'branch' ? 'BRANCH' : 'CONTENT_CATEGORY';
      const data = await previewCompareContent(
        sourceCategory.id,
        scopeType,
        selectedTarget.category.id,
        matchRule,
      );
      if (requestGen !== previewRequestGenRef.current) {
        return;
      }
      setPreview(data);
    } catch (err: unknown) {
      if (requestGen !== previewRequestGenRef.current) {
        return;
      }
      setPreview(null);
      setError(extractApiErrorMessage(err, 'Failed to compare content.'));
    } finally {
      setPreviewLoading(false);
    }
  }, [sourceCategory, selectedTarget, matchRule]);

  useEffect(() => {
    if (!open || !sourceCategory) {
      return;
    }
    setSelectedTargetCategoryId(defaultTargetCategoryId);
    setMatchRule('EQUIVALENT');
    setSplitIntoSubcategories(false);
    setPreview(null);
    setError(null);
  }, [open, sourceCategory, defaultTargetCategoryId]);

  useEffect(() => {
    if (!open || !sourceCategory || !selectedTargetCategoryId) {
      return;
    }
    void refreshPreview();
  }, [open, sourceCategory, selectedTargetCategoryId, matchRule, refreshPreview]);

  const handleTreeSelect = (_event: React.SyntheticEvent, nodeId: string) => {
    if (!sourceCategory) return;
    const cat = findCategoryInTree(categoriesTree, nodeId);
    if (!cat) return;
    const target = resolveCompareTargetForCategory(cat, sourceCategory.id);
    if (target) {
      setSelectedTargetCategoryId(cat.id);
    }
  };

  const handleApply = (which: 'matched' | 'unmatched') => {
    if (!preview || !sourceCategory) {
      return;
    }
    const ids =
      which === 'matched' ? preview.matchedContentIds : preview.unmatchedContentIds;
    if (splitIntoSubcategories) {
      onSplitWithContentIds(ids, which);
    } else {
      onSelectContentIds(ids);
    }
    onClose();
  };

  const renderCompareTreeNode = (category: Category): React.ReactNode | null => {
    if (!sourceCategory) return null;
    const isSource = category.id === sourceCategory.id;
    const target = resolveCompareTargetForCategory(category, sourceCategory.id);
    const selectable = Boolean(target);
    if (
      !isSource &&
      !selectable &&
      !categorySubtreeHasSelectableCompareTarget(category, sourceCategory.id)
    ) {
      return null;
    }
    const hasChildren = (category.children?.length ?? 0) > 0;

    const contentCount = countContentRowsInCategorySubtree(category);
    const nameColor = isSource ? 'text.disabled' : selectable ? 'text.primary' : 'text.secondary';
    const label = (
      <CategoryPickerTreeLabel
        category={category}
        contentCount={contentCount}
        nameColor={nameColor}
        nameSuffix={isSource ? ' (this category)' : ''}
      />
    );

    return (
      <TreeItem
        key={category.id}
        nodeId={category.id}
        label={label}
        disabled={isSource}
      >
        {hasChildren &&
          category
            .children!.map(child => renderCompareTreeNode(child))
            .filter(Boolean)}
      </TreeItem>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Compare content and select
        {sourceCategory?.name ? ` — ${sourceCategory.name}` : ''}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Compare rows in <strong>{sourceCategory?.name ?? 'this category'}</strong> to another
          content category or branch. Click a folder (branch) or content category in the tree.
          Selection applies only to rows in this category.
        </Typography>

        {!hasTargets || !documentRoot ? (
          <Alert severity="warning">
            No compare targets in this document (need another content category with rows or a
            branch that contains content).
          </Alert>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              Compare against
            </Typography>
            <Box
              sx={{
                maxHeight: 280,
                overflow: 'auto',
                border: theme => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 0.5,
              }}
            >
              <TreeView
                key={`${documentRoot.id}-${sourceCategory?.id}`}
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                defaultExpanded={defaultExpanded}
                selected={selectedTargetCategoryId || ''}
                onNodeSelect={handleTreeSelect}
              >
                {renderCompareTreeNode(documentRoot)}
              </TreeView>
            </Box>
          </Box>
        )}

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Match rule
          </Typography>
          <RadioGroup
            value={matchRule}
            onChange={e => setMatchRule(e.target.value as CompareContentMatchRule)}
          >
            <FormControlLabel
              value="EQUIVALENT"
              control={<Radio />}
              label="Same subcase and text"
            />
            <FormControlLabel
              value="SUBCASE_ONLY"
              control={<Radio />}
              label="Same subcase only"
            />
          </RadioGroup>
        </FormControl>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {selectedTargetCategoryId && selectedTarget && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 28 }}>
            {previewLoading && <CircularProgress size={20} />}
            <Typography variant="body2">
              {previewLoading
                ? 'Comparing…'
                : preview
                  ? `${preview.matchedCount} matched, ${preview.unmatchedCount} unmatched (${preview.totalInSource} total in this category)`
                  : 'No preview yet.'}
            </Typography>
          </Box>
        )}

        <FormControlLabel
          sx={{ mt: 1, alignItems: 'flex-start' }}
          control={
            <Checkbox
              checked={splitIntoSubcategories}
              onChange={e => setSplitIntoSubcategories(e.target.checked)}
              disabled={!selectedTargetCategoryId || !selectedTarget}
            />
          }
          label={
            <Box>
              <Typography variant="body2">Split into two subcategories</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                When checked, the button you click puts those rows in the first subcategory and the
                rest in the second (same as Split category with a selection). You name both
                subcategories next.
              </Typography>
            </Box>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => handleApply('matched')}
          disabled={!preview || previewLoading}
        >
          {splitIntoSubcategories ? 'Split — matched in first' : 'Select matched'}
        </Button>
        <Button
          variant="contained"
          onClick={() => handleApply('unmatched')}
          disabled={!preview || previewLoading}
        >
          {splitIntoSubcategories ? 'Split — unmatched in first' : 'Select unmatched'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompareContentDialog;
